// AIDEV-NOTE: Window.env is injected by the server-side root loader (getBrowserEnv)
interface WindowWithEnv {
    env?: Record<string, string | undefined>;
}

interface ImportMetaWithEnv {
    env?: { PROD?: boolean };
}

export function isSentryEnabled(): boolean {
    // AIDEV-NOTE: remix-serve runs with NODE_ENV=production in Playwright, but E2E/test
    // requests must not emit real Sentry traffic. Gate on ENV_NAME/PLAYWRIGHT_TEST first.
    if (
        (typeof process !== "undefined" &&
            (process.env.PLAYWRIGHT_TEST === "1" ||
                process.env.ENV_NAME === "test" ||
                process.env.ENV_NAME === "ci")) ||
        (typeof window !== "undefined" &&
            (window as unknown as WindowWithEnv)?.env?.ENV_NAME &&
            ["test", "ci"].includes(
                (window as unknown as WindowWithEnv).env!.ENV_NAME ?? "",
            ))
    ) {
        return false;
    }

    // Browser: prefer window.env flag injected by server
    if (
        typeof window !== "undefined" &&
        (window as unknown as WindowWithEnv)?.env
    ) {
        const flag = (window as unknown as WindowWithEnv).env!.SENTRY_ENABLED;
        if (typeof flag === "string") return flag.toLowerCase() === "true";
        // Fallback to Vite's production flag
        return (
            typeof import.meta !== "undefined" &&
            !!(import.meta as unknown as ImportMetaWithEnv)?.env?.PROD
        );
    }
    // Server: check explicit flag or fallback to NODE_ENV
    const envFlag = process.env.SENTRY_ENABLED;
    if (typeof envFlag === "string") return envFlag.toLowerCase() === "true";
    return process.env.NODE_ENV === "production";
}

export function getSentryDsn(): string | undefined {
    if (
        typeof window !== "undefined" &&
        (window as unknown as WindowWithEnv)?.env
    ) {
        return (window as unknown as WindowWithEnv).env!.SENTRY_DSN;
    }
    return process.env.SENTRY_DSN;
}

export function getBrowserEnvVar(key: string): string | undefined {
    if (
        typeof window !== "undefined" &&
        (window as unknown as WindowWithEnv)?.env
    ) {
        return (window as unknown as WindowWithEnv).env![key];
    }
    return process.env[key];
}

export const SERVER_URL = getBrowserEnvVar("SERVER_URL")!;

// AIDEV-NOTE: Enable analytics in test/ci environments so Playwright can verify events.
// Sentry and Datadog are disabled separately in test mode (see analytics.ts).
export const ENABLE_ANALYTICS =
    (typeof import.meta !== "undefined" && import.meta.env?.PROD) ||
    (typeof process !== "undefined" && process.env.NODE_ENV === "production") ||
    getBrowserEnvVar("ENV_NAME") === "ci" ||
    getBrowserEnvVar("ENV_NAME") === "test" ||
    false;

// AIDEV-NOTE: Helper to check if we're in test mode (for disabling Sentry/Datadog)
export const IS_TEST_MODE =
    getBrowserEnvVar("ENV_NAME") === "ci" ||
    getBrowserEnvVar("ENV_NAME") === "test";
